export const STAGE2_CHANNEL_PUZZLE_POINTS = 100;

export const CHANNEL_OPTIONS = [
  {
    id: "meeting",
    label: "Meeting",
    description: "Best for discussions and complex topics",
  },
  {
    id: "email",
    label: "Email",
    description: "Best for detailed information and formal communication",
  },
  {
    id: "chat",
    label: "Chat",
    description: "Best for quick questions and short updates",
  },
];

export const STAGE2_SCENARIOS = [
  {
    id: "quick_approval",
    text: "You need a quick yes/no on extending the project deadline by one day.",
    hint: "Think about urgency and whether a live discussion is needed.",
    correct: "chat",
    correctExplanation:
      "Chat is ideal for quick approvals — fast, informal, and no calendar invite required.",
    wrongExplanation:
      "This doesn't need a full meeting or a formal email thread. A quick chat message gets the answer faster.",
  },
  {
    id: "quarterly_planning",
    text: "Your team needs to discuss goals, trade-offs, and priorities for next quarter.",
    hint: "Consider whether back-and-forth discussion is essential.",
    correct: "meeting",
    correctExplanation:
      "Quarterly planning involves debate and alignment — a meeting gives everyone space to contribute.",
    wrongExplanation:
      "Complex planning with multiple perspectives needs real-time discussion, not async messages.",
  },
  {
    id: "monthly_report",
    text: "You need to share the monthly sales report with the leadership team.",
    hint: "Think about the audience, urgency, and need for documentation.",
    correct: "email",
    correctExplanation:
      "A detailed report should be emailed so leaders can review data on their own time and keep a record.",
    wrongExplanation:
      "Reports with charts and figures are better documented in email than squeezed into a meeting or chat.",
  },
  {
    id: "project_update",
    text: "Share a brief project status update with your wider team.",
    hint: "How much detail and discussion does this really need?",
    correct: "email",
    correctExplanation:
      "A structured project update belongs in email — everyone gets the same information asynchronously.",
    wrongExplanation:
      "A status update doesn't need a live meeting or a real-time chat ping. Email keeps it documented.",
  },
  {
    id: "design_feedback",
    text: "A colleague asks whether the new logo should use blue or green accents.",
    hint: "Is this a quick opinion or a deep design review?",
    correct: "chat",
    correctExplanation:
      "A quick design preference is perfect for chat — one message, one reply, done.",
    wrongExplanation:
      "Scheduling a meeting or drafting an email for a simple either/or question wastes everyone's time.",
  },
  {
    id: "conflict_resolution",
    text: "Two departments disagree on who owns a customer escalation process.",
    hint: "Will this be resolved with a single message?",
    correct: "meeting",
    correctExplanation:
      "Ownership conflicts need live conversation to surface concerns and agree on next steps.",
    wrongExplanation:
      "Email threads and chat messages often escalate misunderstandings. A focused meeting resolves this faster.",
  },
  {
    id: "policy_reminder",
    text: "Remind the team about the updated expense reimbursement policy and link to the document.",
    hint: "Does everyone need to discuss this, or just read it?",
    correct: "email",
    correctExplanation:
      "Policy reminders with links belong in email so people can reference the document later.",
    wrongExplanation:
      "A meeting or chat for a policy link creates interruption without adding value.",
  },
  {
    id: "where_is_file",
    text: "Someone asks where the latest budget spreadsheet is saved.",
    hint: "How long will this take to answer?",
    correct: "chat",
    correctExplanation:
      "A quick file location question is exactly what chat is for — answer in seconds.",
    wrongExplanation:
      "Don't book a meeting or write a formal email just to share a folder path.",
  },
  {
    id: "brainstorm_features",
    text: "Generate ideas for improving the onboarding experience with your product team.",
    hint: "Does this benefit from live collaboration?",
    correct: "meeting",
    correctExplanation:
      "Brainstorming builds on each other's ideas — a meeting creates the creative energy you need.",
    wrongExplanation:
      "Async email or chat rarely produces the same flow of ideas as a short focused brainstorm session.",
  },
  {
    id: "meeting_time_check",
    text: "Confirm whether 2 PM tomorrow works for a 15-minute check-in.",
    hint: "What's the simplest way to coordinate a time?",
    correct: "chat",
    correctExplanation:
      "Scheduling a quick check-in is a classic chat use case — fast back-and-forth on availability.",
    wrongExplanation:
      "Sending an email or holding a meeting just to find a 15-minute slot is overkill.",
  },
];

export function getChannelLabel(channelId) {
  return CHANNEL_OPTIONS.find((o) => o.id === channelId)?.label || channelId;
}
